/**
 * Workflow Execution Engine
 * 
 * Executes workflow automations with GPT-5 Nano Agent integration.
 * Supports triggers, conditions, actions, AI nodes, and loops.
 */

import { blink } from '@/lib/blink';
import { gpt5NanoAgent } from './gpt5NanoAgent';
import { tracedEmailActions } from './tracedEmailActions';
import { unifiedCalendarService } from './calendar/unifiedCalendarService';
import { validator } from '@/lib/validator';

export interface WorkflowNode {
  id: string;
  type: 'trigger' | 'condition' | 'action' | 'ai' | 'loop' | 'branch';
  title: string;
  description: string;
  config: Record<string, any>;
}

export interface WorkflowDefinition {
  id: string;
  name: string;
  description: string;
  nodes: WorkflowNode[];
  enabled: boolean;
  userId: string;
}

export interface WorkflowExecution {
  workflowId: string;
  executionId: string;
  startTime: Date;
  endTime?: Date;
  status: 'running' | 'completed' | 'failed' | 'paused';
  currentNodeIndex: number;
  nodeResults: Array<{
    nodeId: string;
    status: 'pending' | 'running' | 'success' | 'error';
    startTime: Date;
    endTime?: Date;
    output?: any;
    error?: string;
  }>;
  context: Record<string, any>; // Shared data between nodes
}

export interface TriggerEvent {
  type: 'email_received' | 'schedule' | 'webhook' | 'manual';
  data: any;
  timestamp: Date;
}

export class WorkflowExecutionEngine {
  private activeExecutions = new Map<string, WorkflowExecution>();
  private triggers = new Map<string, WorkflowDefinition[]>();

  /**
   * Register workflow with trigger
   */
  registerWorkflow(workflow: WorkflowDefinition) {
    const triggerNode = workflow.nodes.find(n => n.type === 'trigger');
    if (!triggerNode) {
      throw new Error('Workflow must have at least one trigger node');
    }

    const triggerType = triggerNode.config.triggerType || 'manual';
    
    if (!this.triggers.has(triggerType)) {
      this.triggers.set(triggerType, []);
    }
    
    this.triggers.get(triggerType)!.push(workflow);
    console.log(`✅ Registered workflow "${workflow.name}" with trigger type: ${triggerType}`);
  }

  /**
   * Trigger workflow execution
   */
  async triggerWorkflow(event: TriggerEvent): Promise<string[]> {
    const matchingWorkflows = this.triggers.get(event.type) || [];
    const executionIds: string[] = [];

    for (const workflow of matchingWorkflows) {
      if (!workflow.enabled) continue;

      // Check trigger conditions
      const shouldRun = await this.evaluateTrigger(workflow.nodes[0], event);
      if (!shouldRun) continue;

      const executionId = await this.startExecution(workflow, event);
      executionIds.push(executionId);
    }

    return executionIds;
  }

  /**
   * Start workflow execution
   */
  async startExecution(workflow: WorkflowDefinition, triggerEvent: TriggerEvent): Promise<string> {
    const executionId = `exec_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    
    const execution: WorkflowExecution = {
      workflowId: workflow.id,
      executionId,
      startTime: new Date(),
      status: 'running',
      currentNodeIndex: 0,
      nodeResults: workflow.nodes.map(node => ({
        nodeId: node.id,
        status: 'pending',
        startTime: new Date()
      })),
      context: {
        trigger: triggerEvent.data,
        userId: workflow.userId
      }
    };

    this.activeExecutions.set(executionId, execution);
    console.log(`🚀 Started workflow execution: ${executionId} for workflow: ${workflow.name}`);

    // Execute workflow asynchronously
    this.executeWorkflowInternal(workflow, execution).catch(error => {
      console.error(`Workflow execution failed: ${executionId}`, error);
      execution.status = 'failed';
      execution.endTime = new Date();
    });

    return executionId;
  }

  /**
   * Execute workflow (public API for tests) - converts old format to new
   */
  async executeWorkflow(workflow: any, context: any): Promise<any> {
    try {
      // Convert old workflow format to new format if needed
      let workflowDef: WorkflowDefinition;
      
      if (workflow.actions && !workflow.nodes) {
        // Old format: convert actions array to nodes
        const nodes: WorkflowNode[] = [];
        
        // Add trigger node
        nodes.push({
          id: 'trigger-1',
          type: 'trigger',
          title: workflow.trigger?.type || 'Manual Trigger',
          description: 'Workflow trigger',
          config: workflow.trigger || { triggerType: 'manual' }
        });
        
        // Add action nodes
        if (Array.isArray(workflow.actions)) {
          workflow.actions.forEach((action: any, index: number) => {
            nodes.push({
              id: `action-${index + 1}`,
              type: 'action',
              title: action.type || 'Action',
              description: action.config?.message || 'Workflow action',
              config: action.config || {}
            });
          });
        }
        
        workflowDef = {
          id: workflow.id || `wf-${Date.now()}`,
          name: workflow.name || 'Unnamed Workflow',
          description: workflow.description || '',
          nodes,
          enabled: workflow.isActive !== false,
          userId: workflow.userId || context.userId
        };
      } else if (workflow.nodes) {
        // New format: use as-is
        workflowDef = workflow as WorkflowDefinition;
      } else {
        // No valid workflow structure - create minimal workflow
        workflowDef = {
          id: workflow.id || `wf-${Date.now()}`,
          name: workflow.name || 'Unnamed Workflow',
          description: workflow.description || '',
          nodes: [
            {
              id: 'trigger-1',
              type: 'trigger',
              title: 'Manual Trigger',
              description: 'Workflow trigger',
              config: { triggerType: 'manual' }
            }
          ],
          enabled: true,
          userId: workflow.userId || context.userId
        };
      }
      
      // Create execution
      const executionId = `exec_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      const execution: WorkflowExecution = {
        workflowId: workflowDef.id,
        executionId,
        startTime: new Date(),
        status: 'running',
        currentNodeIndex: 0,
        nodeResults: (workflowDef.nodes || []).map(node => ({
          nodeId: node.id,
          status: 'pending' as const,
          startTime: new Date()
        })),
        context: {
          ...context,
          userId: workflowDef.userId
        }
      };
      
      this.activeExecutions.set(executionId, execution);
      
      // Execute workflow
      await this.executeWorkflowInternal(workflowDef, execution);
      
      return {
        success: true,
        executionId,
        status: execution.status,
        results: execution.nodeResults
      };
    } catch (error) {
      console.error('Workflow execution error:', error);
      throw error;
    }
  }

  /**
   * Execute complete workflow (internal)
   */
  private async executeWorkflowInternal(workflow: WorkflowDefinition, execution: WorkflowExecution) {
    try {
      for (let i = 0; i < workflow.nodes.length; i++) {
        const node = workflow.nodes[i];
        const nodeResult = execution.nodeResults[i];
        
        console.log(`📍 Executing node ${i + 1}/${workflow.nodes.length}: ${node.title}`);
        
        nodeResult.status = 'running';
        nodeResult.startTime = new Date();
        execution.currentNodeIndex = i;

        try {
          // Execute node based on type
          const result = await this.executeNode(node, execution.context);
          
          nodeResult.status = 'success';
          nodeResult.endTime = new Date();
          nodeResult.output = result;

          // Store result in context for next nodes
          execution.context[`node_${node.id}`] = result;
          
          console.log(`✅ Node completed: ${node.title}`);

          // Handle conditional branching
          if (node.type === 'condition' || node.type === 'branch') {
            const shouldContinue = this.evaluateCondition(result, node.config);
            if (!shouldContinue) {
              console.log(`⏭️ Condition not met, skipping remaining nodes`);
              break;
            }
          }
        } catch (error) {
          nodeResult.status = 'error';
          nodeResult.endTime = new Date();
          nodeResult.error = error instanceof Error ? error.message : 'Unknown error';
          
          console.error(`❌ Node failed: ${node.title}`, error);
          
          // Stop execution on error (unless configured to continue)
          if (!node.config.continueOnError) {
            throw error;
          }
        }
      }

      execution.status = 'completed';
      execution.endTime = new Date();
      console.log(`✅ Workflow execution completed: ${execution.executionId}`);

      // Save execution results to database
      await this.saveExecutionResults(execution);
    } catch (error) {
      execution.status = 'failed';
      execution.endTime = new Date();
      console.error(`❌ Workflow execution failed: ${execution.executionId}`, error);
      throw error;
    }
  }

  /**
   * Check if config contains unresolved template variables
   */
  private configHasTemplateVariables(config: any): boolean {
    if (!config || typeof config !== 'object') return false;
    
    const jsonStr = JSON.stringify(config);
    return jsonStr.includes('{{') && jsonStr.includes('}}');
  }

  /**
   * Execute individual node
   */
  private async executeNode(node: WorkflowNode, context: Record<string, any>): Promise<any> {
    // Validate node configuration before execution
    // Skip validation if config contains template variables (they'll be validated after interpolation)
    if (!this.configHasTemplateVariables(node.config)) {
      try {
        validator.validateWorkflowNodeConfigOrThrow(node);
      } catch (error) {
        console.error(`❌ Node validation failed for "${node.title}":`, error);
        throw new Error(`Node validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } else {
      console.log(`⏭️  Skipping pre-execution validation for "${node.title}" (contains template variables)`);
    }

    // Set user context for traced actions
    tracedEmailActions.setUserId(context.userId);

    switch (node.type) {
      case 'trigger':
        return this.executeTriggerNode(node, context);
      
      case 'ai':
        return this.executeAINode(node, context);
      
      case 'condition':
      case 'branch':
        return this.executeConditionNode(node, context);
      
      case 'action':
        return this.executeActionNode(node, context);
      
      case 'loop':
        return this.executeLoopNode(node, context);
      
      default:
        throw new Error(`Unknown node type: ${node.type}`);
    }
  }

  /**
   * Execute trigger node
   */
  private async executeTriggerNode(node: WorkflowNode, context: Record<string, any>): Promise<any> {
    // Trigger node just passes through the trigger data
    return context.trigger;
  }

  /**
   * Execute AI node using GPT-5 Nano Agent
   */
  private async executeAINode(node: WorkflowNode, context: Record<string, any>): Promise<any> {
    console.log(`🤖 Executing AI node: ${node.title}`);
    
    const { config } = node;
    
    // Build prompt with context variables
    const prompt = this.interpolateVariables(config.prompt || '', context);
    
    switch (node.title) {
      case 'AI Classify':
        return await this.aiClassify(prompt, context, config);
      
      case 'AI Extract':
        return await this.aiExtract(prompt, context, config);
      
      case 'AI Generate':
        return await this.aiGenerate(prompt, context, config);
      
      case 'AI Research':
        return await this.aiResearch(prompt, context, config);
      
      case 'AI Analyze':
        return await this.aiAnalyze(prompt, context, config);
      
      default:
        // Generic AI processing
        const response = await gpt5NanoAgent.processUserIntent(prompt);
        if (response.action.type !== 'error') {
          const result = await gpt5NanoAgent.executeAction(response.action);
          return result;
        }
        throw new Error('AI processing failed');
    }
  }

  /**
   * AI Classification with output validation
   */
  private async aiClassify(prompt: string, context: Record<string, any>, config: any): Promise<any> {
    const { object } = await blink.ai.generateObject({
      prompt: prompt || 'Classify the following email into categories',
      schema: {
        type: 'object',
        properties: {
          category: { type: 'string' },
          priority: { type: 'string', enum: ['high', 'medium', 'low'] },
          isUrgent: { type: 'boolean' },
          suggestedTags: { type: 'array', items: { type: 'string' } },
          confidence: { type: 'number' }
        },
        required: ['category', 'priority']
      }
    });
    
    // Validate AI output structure
    const validationResult = validator.validateAIClassificationOutput(object, {
      userId: context.userId,
      workflowId: context.workflowId,
      nodeId: context.currentNodeId,
      actionType: 'ai_classify'
    });
    
    if (!validationResult.valid) {
      console.warn('⚠️ AI Classification output validation failed:', validationResult.errorMessage);
      // Continue with best-effort data but log the issue
    }
    
    return object;
  }

  /**
   * AI Entity Extraction with output validation
   */
  private async aiExtract(prompt: string, context: Record<string, any>, config: any): Promise<any> {
    const { object } = await blink.ai.generateObject({
      prompt: prompt || 'Extract entities from the email',
      schema: {
        type: 'object',
        properties: {
          entities: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                type: { type: 'string', enum: ['person', 'company', 'date', 'location', 'action_item'] },
                value: { type: 'string' },
                confidence: { type: 'number' }
              }
            }
          },
          actionItems: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                task: { type: 'string' },
                dueDate: { type: 'string' },
                priority: { type: 'string' }
              }
            }
          }
        },
        required: ['entities']
      }
    });
    
    // Validate AI output structure
    const validationResult = validator.validateAIExtractionOutput(object, {
      userId: context.userId,
      workflowId: context.workflowId,
      nodeId: context.currentNodeId,
      actionType: 'ai_extract'
    });
    
    if (!validationResult.valid) {
      console.warn('⚠️ AI Extraction output validation failed:', validationResult.errorMessage);
    }
    
    return object;
  }

  /**
   * AI Content Generation with output validation
   */
  private async aiGenerate(prompt: string, context: Record<string, any>, config: any): Promise<any> {
    const { text } = await blink.ai.generateText({
      prompt: prompt || 'Generate a response',
      maxTokens: config.maxTokens || 1000
    });
    
    const output = { generatedText: text };
    
    // Validate AI output structure
    const validationResult = validator.validateAIGenerationOutput(output, {
      userId: context.userId,
      workflowId: context.workflowId,
      nodeId: context.currentNodeId,
      actionType: 'ai_generate'
    });
    
    if (!validationResult.valid) {
      console.warn('⚠️ AI Generation output validation failed:', validationResult.errorMessage);
    }
    
    return output;
  }

  /**
   * AI Research with Web Search and output validation
   */
  private async aiResearch(prompt: string, context: Record<string, any>, config: any): Promise<any> {
    const { text, sources } = await blink.ai.generateText({
      prompt: prompt || 'Research the topic',
      search: true,
      maxTokens: config.maxTokens || 2000
    });
    
    const output = { research: text, sources: sources || [] };
    
    // Validate AI output structure
    const validationResult = validator.validateAIResearchOutput(output, {
      userId: context.userId,
      workflowId: context.workflowId,
      nodeId: context.currentNodeId,
      actionType: 'ai_research'
    });
    
    if (!validationResult.valid) {
      console.warn('⚠️ AI Research output validation failed:', validationResult.errorMessage);
    }
    
    return output;
  }

  /**
   * AI Analysis with output validation
   */
  private async aiAnalyze(prompt: string, context: Record<string, any>, config: any): Promise<any> {
    const { object } = await blink.ai.generateObject({
      prompt: prompt || 'Analyze the data',
      schema: {
        type: 'object',
        properties: {
          analysis: { type: 'string' },
          insights: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                insight: { type: 'string' },
                importance: { type: 'string', enum: ['high', 'medium', 'low'] }
              }
            }
          },
          recommendations: {
            type: 'array',
            items: { type: 'string' }
          },
          confidence: { type: 'number' }
        },
        required: ['analysis']
      }
    });
    
    // Validate AI output structure
    const validationResult = validator.validateAIAnalysisOutput(object, {
      userId: context.userId,
      workflowId: context.workflowId,
      nodeId: context.currentNodeId,
      actionType: 'ai_analyze'
    });
    
    if (!validationResult.valid) {
      console.warn('⚠️ AI Analysis output validation failed:', validationResult.errorMessage);
    }
    
    return object;
  }

  /**
   * Execute condition node
   */
  private async executeConditionNode(node: WorkflowNode, context: Record<string, any>): Promise<any> {
    const { config } = node;
    
    // Evaluate condition based on context
    const condition = config.condition || {};
    const result = this.evaluateCondition(context, condition);
    
    return { conditionMet: result };
  }

  /**
   * Execute action node
   */
  private async executeActionNode(node: WorkflowNode, context: Record<string, any>): Promise<any> {
    console.log(`⚡ Executing action: ${node.title}`);
    
    const { config } = node;
    
    // Check both node.title and config.type for action matching (supports both old and new workflow formats)
    const actionType = node.title.toLowerCase().replace(/\s+/g, '_') || config.type;
    
    switch (node.title) {
      case 'log':
        return await this.logAction(config, context);
      
      case 'Send Email':
      case 'send_email': // Support both formats
        return await this.sendEmail(config, context);
      
      case 'Send Reply':
        return await this.sendReply(config, context);
      
      case 'Send Confirmation':
        return await this.sendEmail(config, context); // Same as Send Email
      
      case 'ai_generate_reply':
        return await this.aiGenerateReply(config, context);
      
      case 'extract_participants':
        return await this.extractParticipants(config, context);
      
      case 'ai_classify_email':
        return await this.aiClassifyEmail(config, context);
      
      case 'Search Related Emails':
      case 'search_related_emails':
        return await this.searchRelatedEmails(config, context);
      
      case 'Conditional Branch':
      case 'conditional_branch':
        return await this.conditionalBranch(config, context);
      
      case 'Create Event':
        return await this.createEvent(config, context);
      
      case 'Store Data':
        return await this.storeData(config, context);
      
      case 'HTTP Request':
        return await this.httpRequest(config, context);
      
      case 'Generate Report':
        return await this.generateReport(config, context);
      
      case 'Wait':
        return await this.waitAction(config);
      
      case 'Custom Code':
        return await this.executeCustomCode(config, context);
      
      case 'generate_briefing':
        return await this.generateBriefing(config, context);
      
      case 'apply_labels':
        return await this.applyLabels(config, context);
      
      case 'extract_contact_info':
        return await this.extractContactInfo(config, context);
      
      case 'create_task':
        return await this.createTask(config, context);
      
      case 'analyze_relationship':
        return await this.analyzeRelationship(config, context);
      
      case 'update_contact_intelligence':
        return await this.updateContactIntelligence(config, context);
      
      default:
        // Fallback: check if actionType matches known actions
        if (actionType === 'send_email') {
          return await this.sendEmail(config, context);
        }
        throw new Error(`Unknown action: ${node.title}`);
    }
  }

  /**
   * Execute loop node
   */
  private async executeLoopNode(node: WorkflowNode, context: Record<string, any>): Promise<any> {
    const { config } = node;
    const items = this.resolveVariable(config.items || [], context);
    const results = [];
    
    for (const item of items) {
      const loopContext = { ...context, loopItem: item };
      
      // Execute loop body (would need child nodes in a real implementation)
      // For now, just collect items
      results.push(item);
    }
    
    return { loopResults: results, count: results.length };
  }

  /**
   * Log Action - Simple logging for testing and debugging
   */
  private async logAction(config: any, context: Record<string, any>): Promise<any> {
    const message = this.interpolateVariables(config.message || 'Log action executed', context);
    console.log(`📝 [Workflow Log]: ${message}`);
    return { success: true, message, logged: true };
  }

  /**
   * Send Email Action
   */
  private async sendEmail(config: any, context: Record<string, any>): Promise<any> {
    // Get user's email account for sending
    const userId = context.userId;
    
    if (!userId) {
      throw new Error('Missing userId in context - authentication required');
    }
    
    // Check if user has connected email accounts
    const emailAccounts = await blink.db.emailAccounts.list({
      where: { userId },
      limit: 1
    });
    
    if (!emailAccounts || emailAccounts.length === 0) {
      throw new Error('No email account connected. Please connect Gmail or Outlook in Settings.');
    }
    
    const account = emailAccounts[0];
    
    // Extract parameters - try multiple sources in order of priority
    // 1. Direct config properties
    // 2. Nested recipient/emailSubject properties
    // 3. Context variables with interpolation
    // 4. Try AI-generated reply from previous nodes
    let to = config.to || config.recipient;
    let subject = config.subject || config.emailSubject || config.title;
    let body = config.body || config.message || config.content || config.text;
    
    // Check if this is a reply action - look for AI-generated content
    const aiReply = this.resolveVariable('node_ai_generate_reply.generatedReply', context)
                   || this.resolveVariable('ai_generate_reply.generatedReply', context);
    const aiReplyTo = this.resolveVariable('node_ai_generate_reply.to', context)
                     || this.resolveVariable('ai_generate_reply.to', context);
    const aiReplySubject = this.resolveVariable('node_ai_generate_reply.subject', context)
                          || this.resolveVariable('ai_generate_reply.subject', context);
    
    if (aiReply) {
      body = body || aiReply;
    }
    if (aiReplyTo) {
      to = to || aiReplyTo;
    }
    if (aiReplySubject) {
      subject = subject || aiReplySubject;
    }
    
    // If still missing, try to get from trigger context
    if (!to && context.trigger?.from) {
      to = context.trigger.from;
    }
    if (!subject && context.trigger?.subject) {
      subject = context.trigger.subject.startsWith('Re:') ? context.trigger.subject : `Re: ${context.trigger.subject}`;
    }
    if (!body) {
      body = config.defaultBody || 'Email sent from workflow automation.';
    }
    
    // Interpolate variables
    to = this.interpolateVariables(to, context);
    subject = this.interpolateVariables(subject, context);
    body = this.interpolateVariables(body, context);
    
    console.log('📧 Send email params (after interpolation):', { 
      to: to || 'missing',
      subject: subject || 'missing',
      body: body ? (body.length > 50 ? body.substring(0, 50) + '...' : body) : 'missing',
      accountId: account.id,
      userId,
      configKeys: Object.keys(config),
      hasTemplateVariables: this.configHasTemplateVariables(config)
    });
    
    // Post-interpolation validation: ensure required fields are present and not template variables
    if (!to || !subject) {
      // Build helpful error message
      const missingFields = [];
      if (!to) missingFields.push('"to"');
      if (!subject) missingFields.push('"subject"');
      
      console.error('❌ Missing email parameters after template interpolation:', { 
        configKeys: Object.keys(config),
        config,
        contextKeys: Object.keys(context),
        extractedTo: to,
        extractedSubject: subject,
        missingFields
      });
      
      throw new Error(
        `Validation failed for Action Node (send_email):\n\n` +
        missingFields.map(field => `⚠️ Missing required field: ${field}`).join('\n') +
        `\n\nPlease correct these issues and try again.`
      );
    }
    
    // Check if template variables were not properly interpolated
    if (String(to).includes('{{') || String(subject).includes('{{')) {
      console.warn('⚠️ Unresolved template variables detected:', { to, subject });
      throw new Error(
        `Validation failed for Action Node (send_email):\n\n` +
        `⚠️ Template variables not resolved. Please check your workflow context.\n` +
        `- to: ${to}\n` +
        `- subject: ${subject}\n\n` +
        `Ensure the trigger or previous nodes provide the required data.`
      );
    }
    
    // Use tracedEmailActions with proper authentication
    tracedEmailActions.setUserId(userId);
    
    const result = await tracedEmailActions.sendEmail({
      to: Array.isArray(to) ? to : [to],
      subject,
      body,
      htmlBody: config.useHtml ? body : undefined,
      accountId: account.id
    });
    
    return { success: true, messageId: result.messageId, to, subject };
  }

  /**
   * Send Reply Action (reply to an email)
   */
  private async sendReply(config: any, context: Record<string, any>): Promise<any> {
    const to = this.interpolateVariables(config.to, context);
    const subject = this.interpolateVariables(config.subject || '', context);
    const body = this.interpolateVariables(config.body || '', context);
    
    // If subject doesn't start with Re:, add it
    const replySubject = subject.startsWith('Re:') ? subject : `Re: ${subject}`;
    
    const result = await tracedEmailActions.sendEmail({
      to: Array.isArray(to) ? to : [to],
      subject: replySubject,
      body,
      htmlBody: config.useHtml ? body : undefined,
      inReplyTo: context.trigger?.id || config.inReplyTo // Thread continuation
    });
    
    return { success: true, messageId: result.messageId, type: 'reply' };
  }

  /**
   * AI Generate Reply Action
   */
  private async aiGenerateReply(config: any, context: Record<string, any>): Promise<any> {
    // Get email context
    const emailSubject = this.interpolateVariables(config.subject || context.trigger?.subject || '', context);
    const emailBody = this.interpolateVariables(config.body || context.trigger?.body || '', context);
    const emailFrom = this.interpolateVariables(config.from || context.trigger?.from || '', context);
    
    // Generate reply using AI
    const { text } = await blink.ai.generateText({
      prompt: `Generate a professional email reply to the following email:

From: ${emailFrom}
Subject: ${emailSubject}
Body: ${emailBody}

Generate a concise, professional reply addressing the main points of the email.`,
      maxTokens: config.maxTokens || 500
    });
    
    return { 
      success: true, 
      generatedReply: text,
      to: emailFrom,
      subject: `Re: ${emailSubject}`
    };
  }

  /**
   * Extract Participants Action
   */
  private async extractParticipants(config: any, context: Record<string, any>): Promise<any> {
    const emailBody = this.interpolateVariables(config.body || context.trigger?.body || '', context);
    const emailSubject = this.interpolateVariables(config.subject || context.trigger?.subject || '', context);
    
    // Use AI to extract participant information
    const { object } = await blink.ai.generateObject({
      prompt: `Extract all participants/attendees mentioned in this email:

Subject: ${emailSubject}
Body: ${emailBody}

Extract names, email addresses, and roles if mentioned.`,
      schema: {
        type: 'object',
        properties: {
          participants: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                name: { type: 'string' },
                email: { type: 'string' },
                role: { type: 'string' }
              }
            }
          },
          totalCount: { type: 'number' }
        },
        required: ['participants']
      }
    });
    
    return { 
      success: true, 
      participants: object.participants,
      count: object.totalCount || object.participants.length
    };
  }

  /**
   * AI Classify Email Action
   */
  private async aiClassifyEmail(config: any, context: Record<string, any>): Promise<any> {
    const emailSubject = this.interpolateVariables(config.subject || context.trigger?.subject || '', context);
    const emailBody = this.interpolateVariables(config.body || context.trigger?.body || '', context);
    const emailFrom = this.interpolateVariables(config.from || context.trigger?.from || '', context);
    
    // Use AI to classify email
    const { object } = await blink.ai.generateObject({
      prompt: `Classify this email:

From: ${emailFrom}
Subject: ${emailSubject}
Body: ${emailBody}

Determine the category, priority, sentiment, and whether it requires urgent attention.`,
      schema: {
        type: 'object',
        properties: {
          category: { 
            type: 'string',
            enum: ['work', 'personal', 'newsletter', 'promotional', 'social', 'spam', 'other']
          },
          priority: { 
            type: 'string', 
            enum: ['high', 'medium', 'low'] 
          },
          sentiment: { 
            type: 'string', 
            enum: ['positive', 'neutral', 'negative', 'urgent'] 
          },
          isUrgent: { type: 'boolean' },
          requiresResponse: { type: 'boolean' },
          suggestedTags: { 
            type: 'array', 
            items: { type: 'string' } 
          },
          confidence: { type: 'number' },
          summary: { type: 'string' }
        },
        required: ['category', 'priority', 'sentiment', 'isUrgent']
      }
    });
    
    return { 
      success: true,
      classification: object
    };
  }

  /**
   * Create Calendar Event Action
   */
  private async createEvent(config: any, context: Record<string, any>): Promise<any> {
    const title = this.interpolateVariables(config.title || '', context);
    const description = this.interpolateVariables(config.description || '', context);
    
    const event = await unifiedCalendarService.createEvent({
      title,
      description,
      start: new Date(config.start),
      end: new Date(config.end),
      attendees: config.attendees || [],
      location: config.location,
      isOnlineMeeting: config.isOnlineMeeting
    });
    
    return { success: true, eventId: event.id };
  }

  /**
   * Store Data Action
   */
  private async storeData(config: any, context: Record<string, any>): Promise<any> {
    const table = config.table || 'workflow_data';
    const data = this.interpolateVariables(config.data, context);
    
    // Store to appropriate table based on config
    // For now, just return success
    return { success: true, stored: data };
  }

  /**
   * HTTP Request Action
   */
  private async httpRequest(config: any, context: Record<string, any>): Promise<any> {
    const url = this.interpolateVariables(config.url || '', context);
    const method = config.method || 'GET';
    const body = config.body ? this.interpolateVariables(config.body, context) : undefined;
    
    const response = await fetch(url, {
      method,
      headers: config.headers || {},
      body: body ? JSON.stringify(body) : undefined
    });
    
    const result = await response.json();
    return { success: response.ok, status: response.status, data: result };
  }

  /**
   * Generate Report Action
   */
  private async generateReport(config: any, context: Record<string, any>): Promise<any> {
    const template = this.interpolateVariables(config.template || '', context);
    
    // Use AI to generate report
    const { text } = await blink.ai.generateText({
      prompt: `Generate a report based on this template and data:\n\nTemplate: ${template}\n\nData: ${JSON.stringify(context, null, 2)}`,
      maxTokens: 2000
    });
    
    return { success: true, report: text };
  }

  /**
   * Wait Action
   */
  private async waitAction(config: any): Promise<any> {
    const delay = config.delay || 1000;
    await new Promise(resolve => setTimeout(resolve, delay));
    return { success: true, waited: delay };
  }

  /**
   * Execute Custom Code
   */
  private async executeCustomCode(config: any, context: Record<string, any>): Promise<any> {
    const code = config.code || '';
    
    // Create safe execution context
    const sandbox = {
      context,
      console: {
        log: (...args: any[]) => console.log('[Custom Code]', ...args)
      },
      blink,
      gpt5NanoAgent,
      tracedEmailActions
    };
    
    // Execute code (in production, use VM or Web Workers for safety)
    try {
      const func = new Function('sandbox', `
        with (sandbox) {
          return (async () => {
            ${code}
          })();
        }
      `);
      
      const result = await func(sandbox);
      return { success: true, result };
    } catch (error) {
      throw new Error(`Custom code execution failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Search Related Emails Action
   */
  private async searchRelatedEmails(config: any, context: Record<string, any>): Promise<any> {
    const userId = context.userId;
    
    if (!userId) {
      throw new Error('Missing userId in context');
    }
    
    // Get search query from config or context
    const searchQuery = this.interpolateVariables(config.query || config.searchQuery || '', context);
    const subject = this.interpolateVariables(config.subject || context.trigger?.subject || '', context);
    const from = this.interpolateVariables(config.from || context.trigger?.from || '', context);
    
    // Build search query
    let finalQuery = searchQuery;
    if (!finalQuery && subject) {
      finalQuery = subject.replace(/^(Re:|Fwd:)\s*/i, '').trim();
    }
    
    console.log(`🔍 Searching related emails for: "${finalQuery}"`);
    
    // Search using tracedEmailActions
    tracedEmailActions.setUserId(userId);
    const results = await tracedEmailActions.searchEmails(finalQuery, {
      limit: config.limit || 10,
      from: from || undefined
    });
    
    return {
      success: true,
      emails: results.emails || [],
      count: results.total || 0,
      query: finalQuery
    };
  }

  /**
   * Generate Briefing Action
   */
  private async generateBriefing(config: any, context: Record<string, any>): Promise<any> {
    // Get context data for briefing
    const participants = this.resolveVariable('node_extract_participants.participants', context) || [];
    const relatedEmails = this.resolveVariable('node_search_related_emails.emails', context) || [];
    const eventDetails = context.trigger || {};
    
    // Generate briefing using AI
    const { text } = await blink.ai.generateText({
      prompt: `Generate a meeting briefing based on the following information:

Event: ${eventDetails.title || 'Meeting'}
Date: ${eventDetails.start || 'TBD'}

Participants (${participants.length}):
${participants.map((p: any) => `- ${p.name || p.email}`).join('\n')}

Related Email Context (${relatedEmails.length} emails):
${relatedEmails.slice(0, 5).map((e: any) => `- ${e.subject || 'No subject'}`).join('\n')}

Generate a concise briefing covering:
1. Meeting purpose and context
2. Key participants and their roles
3. Recent email discussions
4. Suggested agenda items
5. Action items to prepare`,
      maxTokens: config.maxTokens || 1500
    });
    
    return {
      success: true,
      briefing: text,
      participantCount: participants.length,
      emailCount: relatedEmails.length
    };
  }

  /**
   * Apply Labels Action
   */
  private async applyLabels(config: any, context: Record<string, any>): Promise<any> {
    const userId = context.userId;
    
    if (!userId) {
      throw new Error('Missing userId in context');
    }
    
    // Get email ID and labels to apply
    // Try multiple sources for email ID
    let emailId = config.emailId 
                 || context.trigger?.id 
                 || context.trigger?.emailId
                 || context.trigger?.messageId
                 || this.resolveVariable('trigger.id', context)
                 || this.resolveVariable('email.id', context);
    
    emailId = this.interpolateVariables(emailId, context);
    
    // Get labels from config or context - PROPERLY RESOLVE TEMPLATE VARIABLES
    let labelsRaw = config.labels || this.resolveVariable('node_ai_classify_email.classification.suggestedTags', context) || [];
    
    // Interpolate template variables in labels string
    labelsRaw = this.interpolateVariables(labelsRaw, context);
    
    // If still a string (template wasn't resolved), try other node references
    if (typeof labelsRaw === 'string' && labelsRaw.includes('{{')) {
      // Template variable wasn't resolved, try alternative paths
      labelsRaw = this.resolveVariable('node_action-1.classification.suggestedTags', context)
               || this.resolveVariable('classification.suggestedTags', context)
               || [];
    }
    
    const category = this.resolveVariable('node_ai_classify_email.classification.category', context)
                   || this.resolveVariable('node_action-1.classification.category', context);
    
    if (!emailId) {
      console.warn('⚠️ No email ID found in context for apply_labels. Available context keys:', Object.keys(context));
      console.warn('⚠️ Trigger data:', context.trigger);
      throw new Error('Missing email ID to apply labels');
    }
    
    // Convert labels to array and filter out invalid values
    let labelArray = Array.isArray(labelsRaw) ? labelsRaw : [labelsRaw];
    labelArray = labelArray.filter(label => {
      // Filter out empty, null, undefined, and unresolved template variables
      if (!label || typeof label !== 'string') return false;
      if (label.includes('{{') && label.includes('}}')) {
        console.warn(`⚠️ Unresolved template variable detected in label: ${label}`);
        return false;
      }
      return true;
    });
    
    if (labelArray.length === 0) {
      console.warn('⚠️ No valid labels to apply after filtering');
      return {
        success: true,
        emailId,
        appliedLabels: [],
        category,
        count: 0,
        message: 'No valid labels to apply'
      };
    }
    
    console.log(`🏷️ Applying ${labelArray.length} labels to email ${emailId}:`, { labels: labelArray, category });
    
    // Deduplicate labels before processing
    const uniqueLabels = [...new Set(labelArray)];
    
    // Store tags in database with proper deduplication
    const appliedTags = [];
    
    for (const label of uniqueLabels) {
      try {
        // Check if tag exists
        const existingTags = await blink.db.tags.list({
          where: { userId, name: label },
          limit: 1
        });
        
        let tagId: string;
        
        if (existingTags && existingTags.length > 0) {
          tagId = existingTags[0].id;
          console.log(`✓ Using existing tag: ${label} (${tagId})`);
        } else {
          // Create new tag
          const newTag = await blink.db.tags.create({
            id: `tag_${Date.now()}_${Math.random().toString(36).substring(7)}`,
            userId,
            name: label,
            color: this.getTagColor(category),
            createdAt: new Date().toISOString()
          });
          tagId = newTag.id;
          console.log(`✓ Created new tag: ${label} (${tagId})`);
        }
        
        // Check if taggable relationship already exists
        const existingTaggables = await blink.db.taggables.list({
          where: { 
            tagId, 
            taggableType: 'email', 
            taggableId: emailId 
          },
          limit: 1
        });
        
        if (existingTaggables && existingTaggables.length > 0) {
          console.log(`✓ Tag already applied to email: ${label}`);
        } else {
          // Create taggable relationship
          await blink.db.taggables.create({
            id: `taggable_${Date.now()}_${Math.random().toString(36).substring(7)}`,
            tagId,
            taggableType: 'email',
            taggableId: emailId,
            createdAt: new Date().toISOString()
          });
          console.log(`✓ Applied tag to email: ${label}`);
        }
        
        appliedTags.push(label);
      } catch (error) {
        console.error(`❌ Failed to apply label "${label}":`, error);
        // Continue with other labels instead of failing completely
      }
    }
    
    return {
      success: true,
      emailId,
      appliedLabels: appliedTags,
      category,
      count: appliedTags.length
    };
  }

  /**
   * Get tag color based on category
   */
  private getTagColor(category?: string): string {
    const colorMap: Record<string, string> = {
      work: '#3B82F6',
      personal: '#10B981',
      urgent: '#EF4444',
      newsletter: '#8B5CF6',
      promotional: '#F59E0B',
      social: '#EC4899',
      spam: '#6B7280',
      default: '#6366F1'
    };
    
    return colorMap[category?.toLowerCase() || 'default'] || colorMap.default;
  }

  /**
   * Extract Contact Info Action
   */
  private async extractContactInfo(config: any, context: Record<string, any>): Promise<any> {
    const userId = context.userId;
    
    if (!userId) {
      throw new Error('Missing userId in context');
    }
    
    // Get email data to extract contacts from
    const emailSubject = this.interpolateVariables(config.subject || context.trigger?.subject || '', context);
    const emailBody = this.interpolateVariables(config.body || context.trigger?.body || '', context);
    const emailFrom = this.interpolateVariables(config.from || context.trigger?.from || '', context);
    const emailTo = this.interpolateVariables(config.to || context.trigger?.to || '', context);
    const emailCc = this.interpolateVariables(config.cc || context.trigger?.cc || '', context);
    
    console.log(`👤 Extracting contact info from email: "${emailSubject}"`);
    
    // Use AI to extract comprehensive contact information
    const { object } = await blink.ai.generateObject({
      prompt: `Extract detailed contact information from this email:

From: ${emailFrom}
To: ${emailTo}
CC: ${emailCc}
Subject: ${emailSubject}
Body: ${emailBody}

Extract all contacts mentioned including:
- Email addresses
- Names
- Phone numbers
- Companies/Organizations
- Job titles
- Social media handles
- Any other relevant contact details`,
      schema: {
        type: 'object',
        properties: {
          contacts: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                email: { type: 'string' },
                name: { type: 'string' },
                company: { type: 'string' },
                jobTitle: { type: 'string' },
                phone: { type: 'string' },
                source: { type: 'string' }
              }
            }
          },
          primaryContact: {
            type: 'object',
            properties: {
              email: { type: 'string' },
              name: { type: 'string' },
              relationship: { type: 'string' }
            }
          }
        },
        required: ['contacts']
      }
    });
    
    // Store extracted contacts in database
    const savedContacts = [];
    
    for (const contact of object.contacts || []) {
      if (!contact.email) continue;
      
      try {
        // Check if contact already exists
        const existingContacts = await blink.db.contacts.list({
          where: { userId, email: contact.email },
          limit: 1
        });
        
        let contactRecord;
        
        if (existingContacts && existingContacts.length > 0) {
          // Update existing contact
          contactRecord = existingContacts[0];
          
          await blink.db.contacts.update(contactRecord.id, {
            name: contact.name || contactRecord.name,
            company: contact.company || contactRecord.company,
            lastInteractionAt: new Date().toISOString(),
            totalInteractions: (Number(contactRecord.totalInteractions) || 0) + 1,
            updatedAt: new Date().toISOString()
          });
        } else {
          // Create new contact
          contactRecord = await blink.db.contacts.create({
            id: `contact_${Date.now()}_${Math.random().toString(36).substring(7)}`,
            userId,
            email: contact.email,
            name: contact.name || contact.email,
            company: contact.company,
            relationshipType: 'professional',
            engagementLevel: 'low',
            totalInteractions: 1,
            lastInteractionAt: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
        }
        
        savedContacts.push(contactRecord);
      } catch (error) {
        console.error(`Failed to save contact ${contact.email}:`, error);
      }
    }
    
    return {
      success: true,
      contacts: savedContacts,
      extractedCount: object.contacts?.length || 0,
      savedCount: savedContacts.length,
      primaryContact: object.primaryContact
    };
  }

  /**
   * Create Task Action
   */
  private async createTask(config: any, context: Record<string, any>): Promise<any> {
    const userId = context.userId;
    
    if (!userId) {
      throw new Error('Missing userId in context');
    }
    
    // Get task details from config or extract from context
    let title = this.interpolateVariables(config.title || '', context);
    let description = this.interpolateVariables(config.description || '', context);
    let dueDate = this.interpolateVariables(config.dueDate || '', context);
    let priority = this.interpolateVariables(config.priority || 'medium', context);
    
    // If no title provided, try to extract from briefing or email
    if (!title) {
      const briefing = this.resolveVariable('node_generate_briefing.briefing', context);
      const emailSubject = context.trigger?.subject;
      
      if (briefing) {
        // Extract task from briefing using AI
        const { object } = await blink.ai.generateObject({
          prompt: `Extract a task from this briefing:\n\n${briefing}\n\nCreate a concise, actionable task title and description.`,
          schema: {
            type: 'object',
            properties: {
              title: { type: 'string' },
              description: { type: 'string' },
              priority: { type: 'string', enum: ['high', 'medium', 'low'] },
              estimatedDuration: { type: 'string' }
            },
            required: ['title']
          }
        });
        
        title = object.title;
        description = object.description || description;
        priority = object.priority || priority;
      } else if (emailSubject) {
        title = `Follow up: ${emailSubject}`;
      } else {
        title = 'Task from workflow';
      }
    }
    
    console.log(`📋 Creating task: "${title}"`);
    
    // Create task in database
    const task = await blink.db.tasks.create({
      id: `task_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      userId,
      title,
      description: description || '',
      priority: priority as 'high' | 'medium' | 'low',
      status: 'pending',
      source: 'workflow',
      sourceId: context.trigger?.id || null,
      assignedBy: 'ai',
      dueDate: dueDate || null,
      tags: config.tags || null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    // If contact ID is available in context, link task to contact
    const contactId = this.resolveVariable('node_extract_contact_info.primaryContact.id', context) 
                     || this.resolveVariable('node_extract_contact_info.contacts.0.id', context);
    
    if (contactId) {
      try {
        await blink.db.tasks.update(task.id, {
          contactId,
          updatedAt: new Date().toISOString()
        });
      } catch (error) {
        console.warn('Failed to link task to contact:', error);
      }
    }
    
    return {
      success: true,
      task: {
        id: task.id,
        title: task.title,
        priority: task.priority,
        status: task.status,
        dueDate: task.dueDate
      },
      taskId: task.id,
      linkedContactId: contactId || null
    };
  }

  /**
   * Analyze Relationship Action
   */
  private async analyzeRelationship(config: any, context: Record<string, any>): Promise<any> {
    const userId = context.userId;
    
    if (!userId) {
      throw new Error('Missing userId in context');
    }
    
    // Get contact information from context
    const contact = this.resolveVariable('node_extract_contact_info.primaryContact', context)
                   || this.resolveVariable('node_extract_contact_info.contacts.0', context)
                   || context.trigger?.contact;
    
    const emailSubject = this.interpolateVariables(config.subject || context.trigger?.subject || '', context);
    const emailBody = this.interpolateVariables(config.body || context.trigger?.body || '', context);
    const emailFrom = this.interpolateVariables(config.from || context.trigger?.from || '', context);
    
    console.log(`🔍 Analyzing relationship with contact: ${contact?.email || emailFrom}`);
    
    // Use AI to analyze relationship based on communication
    const { object } = await blink.ai.generateObject({
      prompt: `Analyze the professional relationship based on this communication:

From: ${emailFrom}
Subject: ${emailSubject}
Body: ${emailBody}
Contact: ${contact?.name || emailFrom}

Analyze:
- Communication style and tone
- Relationship stage (new, developing, established, strong)
- Engagement level (low, medium, high)
- Trust indicators
- Power dynamics
- Sentiment of interaction
${config.include_sentiment ? '- Detailed sentiment analysis' : ''}

Provide actionable relationship insights.`,
      schema: {
        type: 'object',
        properties: {
          relationshipStage: {
            type: 'string',
            enum: ['new', 'developing', 'established', 'strong', 'at_risk']
          },
          engagementLevel: {
            type: 'string',
            enum: ['low', 'medium', 'high']
          },
          trustLevel: {
            type: 'string',
            enum: ['building', 'moderate', 'high']
          },
          communicationStyle: { type: 'string' },
          sentiment: {
            type: 'string',
            enum: ['positive', 'neutral', 'negative', 'mixed']
          },
          powerDynamic: {
            type: 'string',
            enum: ['peer', 'authority', 'subordinate', 'equal']
          },
          insights: {
            type: 'array',
            items: { type: 'string' }
          },
          recommendations: {
            type: 'array',
            items: { type: 'string' }
          },
          relationshipHealth: {
            type: 'string',
            enum: ['excellent', 'good', 'fair', 'needs_attention']
          }
        },
        required: ['relationshipStage', 'engagementLevel', 'sentiment']
      }
    });
    
    return {
      success: true,
      analysis: object,
      contactEmail: contact?.email || emailFrom,
      contactName: contact?.name
    };
  }

  /**
   * Update Contact Intelligence Action
   */
  private async updateContactIntelligence(config: any, context: Record<string, any>): Promise<any> {
    const userId = context.userId;
    
    if (!userId) {
      throw new Error('Missing userId in context');
    }
    
    // Get analysis from previous node
    const analysis = this.resolveVariable('node_analyze_relationship.analysis', context);
    const contactEmail = this.resolveVariable('node_analyze_relationship.contactEmail', context)
                        || this.resolveVariable('node_extract_contact_info.primaryContact.email', context)
                        || context.trigger?.from;
    
    if (!contactEmail) {
      throw new Error('Missing contact email for intelligence update');
    }
    
    console.log(`📊 Updating contact intelligence for: ${contactEmail}`);
    
    // Find the contact in database
    const contacts = await blink.db.contacts.list({
      where: { userId, email: contactEmail },
      limit: 1
    });
    
    let contact;
    
    if (!contacts || contacts.length === 0) {
      // Contact doesn't exist - create it
      console.log(`📝 Contact not found. Creating new contact: ${contactEmail}`);
      
      const contactName = this.resolveVariable('node_extract_contact_info.primaryContact.name', context)
                         || this.resolveVariable('node_analyze_relationship.contactName', context)
                         || context.trigger?.senderName
                         || contactEmail.split('@')[0];
      
      contact = await blink.db.contacts.create({
        id: `contact_${Date.now()}_${Math.random().toString(36).substring(7)}`,
        userId,
        email: contactEmail,
        name: contactName,
        relationshipType: 'professional',
        engagementLevel: 'low',
        totalInteractions: 1,
        lastInteractionAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      console.log(`✅ Created new contact: ${contact.id}`);
    } else {
      contact = contacts[0];
      console.log(`✅ Found existing contact: ${contact.id}`);
    }
    
    // Update contact with intelligence data
    await blink.db.contacts.update(contact.id, {
      relationshipStage: analysis?.relationshipStage || contact.relationshipStage,
      relationshipHealth: analysis?.relationshipHealth || contact.relationshipHealth,
      engagementLevel: analysis?.engagementLevel || contact.engagementLevel,
      trustLevel: analysis?.trustLevel || contact.trustLevel,
      communicationStyle: analysis?.communicationStyle || contact.communicationStyle,
      powerDynamic: analysis?.powerDynamic || contact.powerDynamic,
      lastInteractionAt: new Date().toISOString(),
      totalInteractions: (Number(contact.totalInteractions) || 0) + 1,
      updatedAt: new Date().toISOString()
    });
    
    // Store detailed intelligence analysis
    const intelligenceRecord = await blink.db.contactIntelligence.create({
      id: `ci_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      contactId: contact.id,
      userId,
      analysisType: 'relationship_analysis',
      relationshipAnalysis: JSON.stringify(analysis),
      behavioralInsights: JSON.stringify(analysis?.insights || []),
      actionRecommendations: JSON.stringify(analysis?.recommendations || []),
      confidenceScore: 85, // Default confidence
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    return {
      success: true,
      contactId: contact.id,
      contactEmail: contact.email,
      intelligenceId: intelligenceRecord.id,
      analysis,
      updated: true
    };
  }

  /**
   * Conditional Branch Action
   */
  private async conditionalBranch(config: any, context: Record<string, any>): Promise<any> {
    // Evaluate the condition
    const condition = config.condition || {};
    const field = this.interpolateVariables(condition.field || '', context);
    const operator = condition.operator || 'equals';
    const value = this.interpolateVariables(condition.value || '', context);
    
    console.log(`🔀 Evaluating condition: ${field} ${operator} ${value}`);
    
    // Get the actual value to compare
    const actualValue = this.resolveVariable(field, context);
    
    let conditionMet = false;
    
    switch (operator) {
      case 'equals':
        conditionMet = actualValue == value;
        break;
      case 'not_equals':
        conditionMet = actualValue != value;
        break;
      case 'contains':
        conditionMet = String(actualValue).toLowerCase().includes(String(value).toLowerCase());
        break;
      case 'greater_than':
        conditionMet = Number(actualValue) > Number(value);
        break;
      case 'less_than':
        conditionMet = Number(actualValue) < Number(value);
        break;
      case 'exists':
        conditionMet = actualValue !== undefined && actualValue !== null && actualValue !== '';
        break;
      case 'is_empty':
        conditionMet = !actualValue || actualValue === '' || (Array.isArray(actualValue) && actualValue.length === 0);
        break;
      default:
        conditionMet = true;
    }
    
    console.log(`✓ Condition result: ${conditionMet ? 'TRUE' : 'FALSE'}`);
    
    return {
      success: true,
      conditionMet,
      field,
      operator,
      value,
      actualValue,
      branch: conditionMet ? 'true' : 'false'
    };
  }

  /**
   * Evaluate trigger conditions
   */
  private async evaluateTrigger(triggerNode: WorkflowNode, event: TriggerEvent): Promise<boolean> {
    // Check trigger configuration matches event
    const config = triggerNode.config;
    
    if (config.emailFilter) {
      // Email-specific filters
      if (config.fromFilter && event.data.from !== config.fromFilter) return false;
      if (config.subjectFilter && !event.data.subject?.includes(config.subjectFilter)) return false;
      if (config.urgentOnly && !event.data.isUrgent) return false;
    }
    
    if (config.schedule) {
      // Schedule-specific filters
      const now = new Date();
      // Would check cron schedule here
    }
    
    return true;
  }

  /**
   * Evaluate condition
   */
  private evaluateCondition(context: any, condition: any): boolean {
    if (!condition.field) return true;
    
    const value = this.resolveVariable(condition.field, context);
    const compareValue = condition.value;
    
    switch (condition.operator) {
      case 'equals':
        return value === compareValue;
      case 'not_equals':
        return value !== compareValue;
      case 'contains':
        return String(value).includes(compareValue);
      case 'greater_than':
        return Number(value) > Number(compareValue);
      case 'less_than':
        return Number(value) < Number(compareValue);
      default:
        return true;
    }
  }

  /**
   * Interpolate variables in strings
   */
  private interpolateVariables(template: any, context: Record<string, any>): any {
    if (typeof template !== 'string') return template;
    
    return template.replace(/\{\{([^}]+)\}\}/g, (match, variable) => {
      return this.resolveVariable(variable.trim(), context) || match;
    });
  }

  /**
   * Resolve variable from context
   */
  private resolveVariable(path: string, context: Record<string, any>): any {
    const parts = path.split('.');
    let value = context;
    
    for (const part of parts) {
      value = value?.[part];
      if (value === undefined) break;
    }
    
    return value;
  }

  /**
   * Get execution status
   */
  getExecutionStatus(executionId: string): WorkflowExecution | undefined {
    return this.activeExecutions.get(executionId);
  }

  /**
   * Save execution results to database
   */
  private async saveExecutionResults(execution: WorkflowExecution) {
    try {
      console.log(`💾 Saving execution results: ${execution.executionId}`);
      
      const executionTimeMs = execution.endTime && execution.startTime 
        ? execution.endTime.getTime() - execution.startTime.getTime()
        : 0;
      
      // Save to workflow_executions table
      await blink.db.workflowExecutions.create({
        id: execution.executionId,
        workflowId: execution.workflowId,
        userId: execution.context.userId,
        status: execution.status,
        startedAt: execution.startTime.toISOString(),
        completedAt: execution.endTime?.toISOString() || null,
        triggerType: execution.context.trigger?.type || 'manual',
        triggerData: JSON.stringify(execution.context.trigger || {}),
        nodeResults: JSON.stringify(execution.nodeResults),
        errorMessage: execution.nodeResults
          .filter(r => r.status === 'error')
          .map(r => r.error)
          .join('; ') || null,
        executionTimeMs,
        createdAt: new Date().toISOString()
      });
      
      console.log(`✅ Saved execution history: ${execution.executionId}`);
    } catch (error) {
      console.error('Failed to save execution results:', error);
      // Don't throw - execution was successful, just logging failed
    }
  }

  /**
   * Clean up old executions
   */
  cleanupOldExecutions(maxAge: number = 24 * 60 * 60 * 1000) {
    const now = Date.now();
    
    for (const [executionId, execution] of this.activeExecutions.entries()) {
      const age = now - execution.startTime.getTime();
      
      if (age > maxAge && execution.status !== 'running') {
        this.activeExecutions.delete(executionId);
      }
    }
  }
}

/**
 * Singleton instance
 */
export const workflowExecutionEngine = new WorkflowExecutionEngine();

/**
 * Quick Start Templates with Real Implementation
 */
export const QUICK_START_TEMPLATES: WorkflowDefinition[] = [
  {
    id: 'auto-reply-urgent',
    name: 'Auto-Reply to Urgent Emails',
    description: 'Automatically sends acknowledgment for high-priority emails',
    enabled: true,
    userId: '', // Set on registration
    nodes: [
      {
        id: 'trigger-1',
        type: 'trigger',
        title: 'Email Received',
        description: 'New email arrives',
        config: {
          triggerType: 'email_received',
          urgentOnly: true
        }
      },
      {
        id: 'ai-1',
        type: 'ai',
        title: 'AI Classify',
        description: 'Classify email intent and urgency',
        config: {
          model: 'gpt-5',
          prompt: 'Classify this email: {{trigger.subject}} - {{trigger.snippet}}. Is it urgent? What is the intent?'
        }
      },
      {
        id: 'condition-1',
        type: 'condition',
        title: 'Check Priority',
        description: 'Filter high priority',
        config: {
          condition: {
            field: 'node_ai-1.priority',
            operator: 'equals',
            value: 'high'
          }
        }
      },
      {
        id: 'ai-2',
        type: 'ai',
        title: 'AI Generate',
        description: 'Generate response',
        config: {
          model: 'gpt-5',
          prompt: 'Generate a professional auto-reply acknowledging receipt of this urgent email: {{trigger.subject}}. Keep it brief and professional.'
        }
      },
      {
        id: 'action-1',
        type: 'action',
        title: 'Send Reply',
        description: 'Send automated reply',
        config: {
          to: '{{trigger.from}}',
          subject: 'Re: {{trigger.subject}}',
          body: '{{node_ai-2.generatedText}}'
        }
      }
    ]
  },
  {
    id: 'newsletter-organizer',
    name: 'Newsletter Organizer',
    description: 'Categorizes and archives newsletters automatically',
    enabled: true,
    userId: '',
    nodes: [
      {
        id: 'trigger-1',
        type: 'trigger',
        title: 'Newsletter Received',
        description: 'Newsletter detected',
        config: {
          triggerType: 'email_received',
          emailFilter: { subjectFilter: 'newsletter' }
        }
      },
      {
        id: 'ai-1',
        type: 'ai',
        title: 'AI Classify',
        description: 'Identify newsletter type',
        config: {
          prompt: 'Classify this newsletter: {{trigger.subject}}. Category? Topics?'
        }
      },
      {
        id: 'action-1',
        type: 'action',
        title: 'Store Data',
        description: 'Store newsletter metadata',
        config: {
          table: 'newsletters',
          data: {
            category: '{{node_ai-1.category}}',
            topics: '{{node_ai-1.topics}}',
            emailId: '{{trigger.id}}'
          }
        }
      }
    ]
  },
  {
    id: 'meeting-request-handler',
    name: 'Meeting Request Handler',
    description: 'Extracts meeting details and creates calendar events',
    enabled: true,
    userId: '',
    nodes: [
      {
        id: 'trigger-1',
        type: 'trigger',
        title: 'Meeting Invitation',
        description: 'Meeting invitation detected',
        config: {
          triggerType: 'email_received',
          emailFilter: { subjectFilter: 'meeting' }
        }
      },
      {
        id: 'ai-1',
        type: 'ai',
        title: 'AI Extract',
        description: 'Extract meeting details',
        config: {
          prompt: 'Extract meeting details from: {{trigger.subject}} - {{trigger.body}}. Include: title, date, time, attendees, location'
        }
      },
      {
        id: 'action-1',
        type: 'action',
        title: 'Create Event',
        description: 'Add to calendar',
        config: {
          title: '{{node_ai-1.entities.title}}',
          start: '{{node_ai-1.entities.startTime}}',
          end: '{{node_ai-1.entities.endTime}}',
          attendees: '{{node_ai-1.entities.attendees}}',
          location: '{{node_ai-1.entities.location}}'
        }
      },
      {
        id: 'action-2',
        type: 'action',
        title: 'Send Confirmation',
        description: 'Confirm meeting added',
        config: {
          to: '{{trigger.from}}',
          subject: 'Meeting Confirmed: {{node_ai-1.entities.title}}',
          body: 'Meeting has been added to calendar successfully.'
        }
      }
    ]
  },
  {
    id: 'smart-email-tagger',
    name: 'Smart Email Tagger',
    description: 'AI-powered email classification and tagging',
    enabled: true,
    userId: '',
    nodes: [
      {
        id: 'trigger-1',
        type: 'trigger',
        title: 'New Email',
        description: 'Any new email',
        config: {
          triggerType: 'email_received'
        }
      },
      {
        id: 'ai-1',
        type: 'ai',
        title: 'AI Classify',
        description: 'Classify and tag',
        config: {
          prompt: 'Analyze and classify this email: {{trigger.subject}} - {{trigger.snippet}}. Suggest category, priority, and relevant tags.'
        }
      },
      {
        id: 'action-1',
        type: 'action',
        title: 'Store Data',
        description: 'Update email metadata',
        config: {
          table: 'email_metadata',
          data: {
            emailId: '{{trigger.id}}',
            category: '{{node_ai-1.category}}',
            priority: '{{node_ai-1.priority}}',
            tags: '{{node_ai-1.suggestedTags}}',
            isUrgent: '{{node_ai-1.isUrgent}}'
          }
        }
      }
    ]
  }
];